"use client";

import clsx from "clsx";
import type { ComponentProps, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

import { MEDIA_QUERY } from "@widgets/device-info/utils/viewport/constants";

import type { FilterItemType } from "@shared/content-types/types";
import { useMedia } from "@shared/hooks/use-media";

import { Filters } from "../filters";
import { SelectableBadge } from "../selectable-badge";

import s from "./filters-with-multi-badges.module.scss";

export type MultiBadgeItem = {
  uid: string;
  label: string;
};

export interface FiltersWithMultiBadgesProps extends ComponentProps<"div"> {
  className?: string;
  filters: FilterItemType[];
  onChangeFilter: (filter: string) => void;
  hasAllItem?: boolean;
  dropdownFieldLabel?: string;

  /** "Все" бейдж, который означает "без фильтра по бейджам". */
  allBadgeLabel?: string;
  badges: MultiBadgeItem[];
  currentBadges: string[];
  selectedBadges: string[];
  onSelectedBadgesChange: (next: string[]) => void;
  children?: ReactNode;
}

export const FiltersWithMultiBadges = (props: FiltersWithMultiBadgesProps) => {
  const {
    className,
    filters,
    onChangeFilter,
    hasAllItem = true,
    dropdownFieldLabel,
    allBadgeLabel = "Все",
    badges,
    selectedBadges,
    onSelectedBadgesChange,
    children,
    currentBadges,
  } = props;

  const isMobile = useMedia(MEDIA_QUERY.MOBILE, false);
  const [isExpanded, setIsExpanded] = useState(false);
  const allBadgeUids = (badges ?? [])
    .map((b) => b?.uid)
    .filter((uid): uid is string => Boolean(uid));
  const isAllSelected =
    (badges?.length ?? 0) > 0 && selectedBadges.length === 0;
  const [suppressAllVisual, setSuppressAllVisual] = useState(false);
  const suppressAllVisualTimeoutRef = useRef<number | null>(null);

  // Сбрасываем состояние при переходе с мобильного на десктоп
  useEffect(() => {
    if (!isMobile) {
      setIsExpanded(false);
    }
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (suppressAllVisualTimeoutRef.current) {
        window.clearTimeout(suppressAllVisualTimeoutRef.current);
        suppressAllVisualTimeoutRef.current = null;
      }
    };
  }, []);

  const startAllVisualTransition = () => {
    if (suppressAllVisualTimeoutRef.current) {
      window.clearTimeout(suppressAllVisualTimeoutRef.current);
    }

    setSuppressAllVisual(true);
    suppressAllVisualTimeoutRef.current = window.setTimeout(() => {
      setSuppressAllVisual(false);
      suppressAllVisualTimeoutRef.current = null;
    }, 200);
  };

  const stopAllVisualTransition = () => {
    if (suppressAllVisualTimeoutRef.current) {
      window.clearTimeout(suppressAllVisualTimeoutRef.current);
      suppressAllVisualTimeoutRef.current = null;
    }
    setSuppressAllVisual(false);
  };

  // Пока suppress=true, не "схлопываем" UI в "Все", чтобы последний клик успел
  // визуально стать активным (анимации SelectableBadge отыгрывают сами).
  const isAllSelectedForUi = isAllSelected && !suppressAllVisual;

  const handleSelectAll = () => {
    stopAllVisualTransition();
    if (isAllSelected) return;
    onSelectedBadgesChange([]);
  };

  const handleToggleBadge = (uid: string) => {
    stopAllVisualTransition();
    if (isAllSelected) {
      onSelectedBadgesChange([uid]);
      return;
    }

    const isSelected = selectedBadges.includes(uid);

    if (isSelected) {
      const next = selectedBadges.filter((x) => x !== uid);
      if (next.length === 0) {
        onSelectedBadgesChange([]);
        return;
      }
      onSelectedBadgesChange(next);
      return;
    }

    const next = [...selectedBadges, uid];
    if (next.length === allBadgeUids.length) {
      // Пользователь выбрал последний недостающий бейдж:
      // на ~200мс показываем состояние "все категории выбраны",
      // и только потом UI схлопнется в "Все".
      startAllVisualTransition();
      onSelectedBadgesChange(allBadgeUids);
      return;
    }
    onSelectedBadgesChange(next);
  };

  // На мобилке считаем "Все" тоже бейджом: показываем максимум 3 бейджа всего,
  // то есть "Все" + 2 категории, остальное скрываем под "..."
  const maxVisibleBadgesOnMobile = 3;
  const maxVisibleCategoryBadgesOnMobile = maxVisibleBadgesOnMobile - 1;
  const shouldCollapse =
    isMobile &&
    badges.length >= maxVisibleCategoryBadgesOnMobile + 1 &&
    !isExpanded;
  const visibleBadges = shouldCollapse
    ? badges.slice(0, maxVisibleCategoryBadgesOnMobile)
    : badges;
  const hiddenBadgesCount = shouldCollapse
    ? badges.length - maxVisibleCategoryBadgesOnMobile
    : 0;

  return (
    <div className={clsx(s.root, className)}>
      <div className={s.topRow}>
        <Filters
          filters={filters}
          onChangeFilter={onChangeFilter}
          hasAllItem={hasAllItem}
          dropdownFieldLabel={dropdownFieldLabel}
          className={s.filters}
        />
        <div className={s.rightSlot}>{children}</div>
      </div>

      {(badges?.length ?? 0) > 0 && (
        <div className={s.bottomRow}>
          <div className={s.badges}>
            <SelectableBadge
              selected={isAllSelectedForUi}
              onClick={handleSelectAll}
            >
              {allBadgeLabel}
            </SelectableBadge>
            {visibleBadges?.map((b) =>
              b?.uid != null ? (
                <SelectableBadge
                  key={b.uid}
                  selected={
                    !isAllSelectedForUi && selectedBadges.includes(b.uid)
                  }
                  onClick={() => handleToggleBadge(b.uid)}
                  disabled={!currentBadges.includes(b.uid)}
                >
                  {b?.label ?? ""}
                </SelectableBadge>
              ) : null,
            )}
            {hiddenBadgesCount > 0 && (
              <SelectableBadge onClick={() => setIsExpanded(true)}>
                ...
              </SelectableBadge>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

FiltersWithMultiBadges.displayName = "FiltersWithMultiBadges";
